import {
    createTheme,
    defaultVariantColorsResolver,
    virtualColor,
} from '@mantine/core'
import type { CSSVariablesResolver, VariantColorsResolver } from '@mantine/core'

// Single source of truth for the app's Mantine theme. Imported by the app shell
// (RootLayout) AND by isolated component environments (Storybook preview) so a
// component renders identically in both and theming changes can't drift.

// Primary brand (filled/gradient) buttons land on bright mint/lime in dark
// mode, where the default white label/icon is illegible. Route their text
// through a scheme-aware on-accent token (white in light, ink in dark);
// autoContrast can't do this because a virtualColor's fill is a CSS var at
// resolve time. All non-primary colors keep default autoContrast behavior.
export const variantColorResolver: VariantColorsResolver = (input) => {
    const resolved = defaultVariantColorsResolver(input)
    const isPrimary = !input.color || input.color === 'sage'
    if (
        isPrimary &&
        (input.variant === 'filled' || input.variant === 'gradient')
    ) {
        return { ...resolved, color: 'var(--ad-on-primary)' }
    }
    // Mantine's default `light` hover is a ~2% alpha bump — near-invisible.
    // Mix the (scheme-aware) light text color into the hover background so the
    // hover state is clearly perceptible in both light and dark modes.
    if (input.variant === 'light') {
        return {
            ...resolved,
            hover: `color-mix(in srgb, ${resolved.hover}, ${resolved.color} 18%)`,
        }
    }
    return resolved
}

// Poppins (self-hosted, see main.tsx) with a system fallback for the swap
// window / missing glyphs. One stack, reused for body + headings + the
// CopilotKit chat (bridged via --cpk-font-sans in index.css) so the whole app
// shares one typeface. Setting it here makes Mantine emit --mantine-font-family,
// the single source of truth everything else references.
export const FONT_STACK =
    '"Poppins", system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'

export const theme = createTheme({
    // ---------- Typography ----------
    // Poppins is geometric and warm (the "Notion/Superhuman" register the brief
    // asked for). Body weight is 400 and secondary text leans on color (dimmed)
    // rather than the brief's weight-300 — thin weights read poorly at UI sizes,
    // so legibility (web best practice) wins over the brief here. Headings are
    // 600 (Poppins SemiBold) for personality without shouting; the big display
    // numbers ("weight moments") use 700 at their call sites.
    fontFamily: FONT_STACK,
    fontFamilyMonospace:
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    // Explicit type scale (rem on a 16px base) — emitted as
    // --mantine-font-size-* and consumed via size="xs|sm|md|lg|xl" everywhere.
    // md (1rem/16px) is the body default; the ramp is a gentle ~1.13 step so
    // sizes feel related, not jumpy.
    fontSizes: {
        xs: '0.75rem', //  12px — micro labels, badges, metadata
        sm: '0.875rem', // 14px — secondary / dense UI text
        md: '1rem', //     16px — body default
        lg: '1.125rem', // 18px — lead / emphasis
        xl: '1.25rem', //  20px — section intros
    },
    // Tighter as text gets larger (long body wants air; headings want cohesion).
    lineHeights: {
        xs: '1.4',
        sm: '1.45',
        md: '1.55', // comfortable body reading measure
        lg: '1.55',
        xl: '1.5',
    },
    headings: {
        fontFamily: FONT_STACK,
        fontWeight: '600',
        // Per-level scale: a clear display ramp with line-heights that tighten
        // toward the larger sizes. All 600 (SemiBold) for a consistent voice.
        sizes: {
            h1: { fontSize: '2rem', lineHeight: '1.25', fontWeight: '600' }, // 32px
            h2: { fontSize: '1.625rem', lineHeight: '1.3', fontWeight: '600' }, // 26px
            h3: { fontSize: '1.375rem', lineHeight: '1.35', fontWeight: '600' }, // 22px
            h4: { fontSize: '1.125rem', lineHeight: '1.4', fontWeight: '600' }, // 18px
            h5: { fontSize: '1rem', lineHeight: '1.45', fontWeight: '600' }, // 16px
            h6: { fontSize: '0.875rem', lineHeight: '1.5', fontWeight: '600' }, // 14px
        },
    },

    // ---------- Brand ----------
    primaryColor: 'sage',
    // light filled = sage[5] #4E9A80 (spec --brand-action button color; Mantine
    // filled hover steps darker to #3D7A65). dark filled = mint[5].
    primaryShade: { light: 6, dark: 6 },
    // sage filled -> white label; pale `light` variant -> dark text
    autoContrast: true,
    luminanceThreshold: 0.45,
    // Scheme-aware label/icon color for primary filled/gradient (see above).
    variantColorResolver,

    // ---------- Radii (sm 5 / md 10 / lg 14 / xl 18) ----------
    defaultRadius: 'md',
    radius: {
        xs: '0.1875rem', // 3px
        sm: '0.3125rem', // 5px
        md: '0.625rem', // 10px (default)
        lg: '0.875rem', // 14px
        xl: '1.125rem', // 18px
    },

    // ---------- Canvas anchors ----------
    white: '#FDFCF9', // surface -> Cards + --mantine-color-default
    black: '#1E1C18', // ink

    colors: {
        // SAGE — primary / action color. A clear TEAL (~188°, cyan-leaning) so it
        // reads distinct from the warm leaf-green success. filled(6)=#15817A.
        // OKLCH-uniform ladder (shared lightness with every other hue).
        sage: [
            '#E7F7F5',
            '#CCEFEB',
            '#A5DED8',
            '#79CAC2',
            '#52B4AC',
            '#309A92',
            '#15817A',
            '#006660',
            '#004C47',
            '#003531',
        ],
        // PLUM — accent / secondary (sage's triadic partner; nudged toward the
        // brand lavender). filled(5)=#A66AC8. OKLCH-uniform ladder.
        plum: [
            '#F9EFFF',
            '#F2DDFF',
            '#E6C0FE',
            '#D4A1F4',
            '#C084E3',
            '#A66AC8',
            '#8C54AB',
            '#6F3F8B',
            '#542D6A',
            '#3B1D4B',
        ],
        // LIME — brand-identity accent; dark CtaButton gradient end + logo.
        // signature #C8FF00 / #A8D800 at 5/6
        lime: [
            '#F7FFE0',
            '#EEFFB8',
            '#E2FF85',
            '#D6FF52',
            '#CFFF29',
            '#CCFF00',
            '#A8D800',
            '#86AD00',
            '#658200',
            '#4B6100',
        ],
        // ---- Semantic status colors (warm-tuned to fit the parchment/charcoal
        // palette). Use via color="error|warning|success|info" for ANY status
        // meaning (alerts, status badges, destructive actions) instead of raw
        // Mantine names like red/orange/teal. Filled = shade 5; autoContrast
        // picks the label color. Colored TEXT (-text/-light-color) is pinned to
        // a darker shade in cssVariablesResolver for WCAG AA (Mantine otherwise
        // maps it to the too-light filled shade). success is a cooler emerald,
        // distinct from the sage/mint `brand`.
        error: [
            '#FFEFF0',
            '#FFDCDE',
            '#FFBDC2',
            '#FF96A0',
            '#EE7785',
            '#D25B6C',
            '#B34656',
            '#913342',
            '#6F222F',
            '#4F151F',
        ],
        warning: [
            '#FBF2E4',
            '#F6E4C6',
            '#EBCC9C',
            '#DBB26F',
            '#C79948',
            '#AD7E27',
            '#92670D',
            '#745000',
            '#573B00',
            '#3D2800',
        ],
        // LEAF — warm-hued green (~145°), distinct from the cool sage primary.
        // filled(5)=#429E4A.
        success: [
            '#E9F9E9',
            '#CFF1CF',
            '#ABE2AC',
            '#83CF85',
            '#60B965',
            '#429E4A',
            '#2C8536',
            '#1B6A25',
            '#0D4F17',
            '#05370C',
        ],
        info: [
            '#ECF4FE',
            '#D6E8FD',
            '#B7D4F5',
            '#96BDE9',
            '#79A5D7',
            '#5F8BBD',
            '#4A73A1',
            '#375A82',
            '#264362',
            '#182E46',
        ],
        // GRAY — overridden to WARM parchment neutral. 3=#DDD9CF (border),
        // 4=#CECABF (border-2), 6=#8A8578 (ink-3/dimmed), 0=canvas-2 hover
        gray: [
            '#F4F1EB',
            '#EDE9E0',
            '#E4DFD4',
            '#DDD9CF',
            '#CECABF',
            '#B8B3A8',
            '#8A8578',
            '#6E6A5F',
            '#46433C',
            '#2A2823',
        ],
        // DARK — DEEP WARM GRAY. Same OKLCH construction as the rest of the
        // system, at the light parchment's hue (86°) so light + dark neutrals
        // share one temperature — but on a deepened lightness profile so the
        // canvas is darker, and kept very low-chroma (0.007) so the shades read
        // warm, NOT brown (holding higher chroma into the darks is what browns
        // them; chroma here is 0.005). Roles: 6=#1D1C19 (surface), 5=#343330
        // (hover), 4=#51504D (border); 7/8/9 are the deepest neutrals. The
        // CANVAS is NOT a raw scale step — like the light surface (#FDFCF9, not
        // a gray step), --mantine-color-body is lifted to #141310 (just above
        // dark[7]=#0A0907, ~Material's #121212 lightness) to reduce halation:
        // bright text blooms less over a not-quite-black canvas. Keeps the
        // ladder uniform. coolSlate/navy below are cool alternates on the
        // standard ladder; to revert, swap one into `dark` and repoint the dark
        // cssVariablesResolver block.
        dark: [
            '#E8E8E6',
            '#C5C4C1',
            '#9C9B98',
            '#767471',
            '#51504D',
            '#343330',
            '#1D1C19',
            '#0A0907',
            '#030302',
            '#010100',
        ],
        // COOL SLATE (spread) — the previous live dark, kept defined for
        // reference / quick revert. NOT active. Shown on /design.
        coolSlate: [
            '#E4ECF4',
            '#C1CCD6',
            '#93A0AC',
            '#6B7986',
            '#4A5763',
            '#313D49',
            '#1D2934',
            '#0C1721',
            '#030B13',
            '#010409',
        ],
        // NAVY (spread) — the site-matched alternate dark, kept defined in case
        // we revert to a bluer/deeper dark. NOT active: to use, swap this scale
        // into `dark` above and repoint the dark cssVariablesResolver block.
        navy: [
            '#E0ECFC',
            '#BCCCE2',
            '#8CA0BB',
            '#647995',
            '#425771',
            '#2A3D56',
            '#162840',
            '#05162C',
            '#000A1C',
            '#00030F',
        ],
        // ---- Categorical / chart palette (decorative series colors, NOT
        // semantic). Used via lib/chartColors CHART_SEQUENCE. coral & gold are
        // CVD-distinct alongside sage/plum; emerald & magenta are extras that
        // need a non-color cue (legend/label/marker) — see /design.
        coral: [
            '#FFF0EB',
            '#FFDED4',
            '#FFC0AC',
            '#FF9B7C',
            '#ED7D5A',
            '#D1623E',
            '#B34C2B',
            '#91381B',
            '#6E270E',
            '#4F1806',
        ],
        gold: [
            '#F8F4E2',
            '#F1E7C0',
            '#E2D192',
            '#D0B85D',
            '#BBA02B',
            '#A08600',
            '#856E00',
            '#695700',
            '#4E4000',
            '#362C00',
        ],
        emerald: [
            '#E6F9EF',
            '#C8F2DC',
            '#9EE3C1',
            '#6CD0A4',
            '#3DBB89',
            '#00A070',
            '#00855C',
            '#006948',
            '#004E34',
            '#003623',
        ],
        magenta: [
            '#FFEEF8',
            '#FFDAF0',
            '#FCB9E2',
            '#F197D0',
            '#DE7ABB',
            '#C35EA1',
            '#A64987',
            '#85366C',
            '#652551',
            '#481739',
        ],
        // brand = lime (IBM-style split): identity/logo color, NOT the primary
        // action color. On dark surfaces only ("lime never touches parchment").
        brand: virtualColor({ name: 'brand', light: 'lime', dark: 'lime' }),
        // secondary = plum accent.
        secondary: virtualColor({
            name: 'secondary',
            light: 'plum',
            dark: 'plum',
        }),
    },

    // ---------- Minimal, justified component defaults ----------
    components: {
        // Cards are the app's ELEVATED SURFACE. Mantine fills Card with the
        // surface (light --mantine-color-white #FDFCF9 / dark --mantine-color-
        // dark-6 #1D1C19 = --mantine-color-default), so cards lift off the
        // canvas (--mantine-color-body) in BOTH schemes — hairline border +
        // subtle shadow, not heavy. CONVENTION: use <Card> for every content
        // card (so the fill stays consistent); reserve <Paper> for flush insets
        // that intentionally share the canvas (and tinted bands like the
        // dashboard's attention strip). Documented on /design → Surfaces.
        Card: {
            defaultProps: { withBorder: true, radius: 'md', shadow: 'xs' },
        },
        Paper: { defaultProps: { radius: 'md' } },
        // The sidebar (AppShell.Navbar) is intentionally NOT special-cased: it
        // uses Mantine's defaults (body background, scheme-aware border, primary-
        // tinted active NavLink), so it follows the light/dark toggle like the
        // rest of the app.
    },
})

export const cssVariablesResolver: CSSVariablesResolver = () => ({
    variables: {
        '--ad-lime': '#CCFF00', // brand (scheme-independent)
    },
    light: {
        '--mantine-color-body': '#F4F1EB', // canvas (body, AppShell, Paper)
        '--mantine-color-text': '#1E1C18', // ink
        '--mantine-color-dimmed': '#6E6A5F', // ink-3 — AA fix (was #8A8578, 3.3:1)
        '--mantine-color-default': '#FDFCF9', // surface (inputs/default btns)
        '--mantine-color-default-hover': '#EDE9E0', // canvas-2
        '--mantine-color-default-border': '#DDD9CF',
        '--mantine-color-default-color': '#1E1C18',
        '--mantine-color-anchor': '#006660', // sage-7 teal link (AA)
        '--mantine-color-placeholder': '#736F63', // AA
        // Colored TEXT pinned to shade 7 (decoupled from the vivid filled shade
        // 5) so c="..." and light-variant Alert/Badge text pass WCAG AA on parchment.
        '--mantine-color-error-text': '#913342',
        '--mantine-color-error-light-color': '#913342',
        '--mantine-color-warning-text': '#745000',
        '--mantine-color-warning-light-color': '#745000',
        '--mantine-color-success-text': '#1B6A25',
        '--mantine-color-success-light-color': '#1B6A25',
        '--mantine-color-info-text': '#375A82',
        '--mantine-color-info-light-color': '#375A82',
        '--ad-on-primary': '#FDFCF9', // white label on the deep sage primary (shade 6)
    },
    dark: {
        '--mantine-color-body': '#141310', // warm near-black canvas, lifted off pure-dark (was dark[7] #0A0907) so bright text blooms less — ~Material #121212 lightness, kept warm. Cards (dark[6] #1D1C19, with borders) still read as elevated.
        '--mantine-color-text': '#D3D1CD', // warm off-white, ~12.2:1 on the lifted canvas. Halation (bright glyphs blooming over near-black) was the real cause of the "too bright" read — lifting the canvas off pure-dark attacks it at the source, so we keep crisp, readable text here rather than dimming it. Comfortably AAA.
        '--mantine-color-dimmed': '#ADACA7', // secondary text — nudged up from dark[2] #9C9B98 (only 6.7:1) so even dimmed text clears AAA on the BINDING case: cards (dark[6] #1D1C19, lighter than the canvas) where it is 7.5:1; on the canvas it is 8.2:1. Still clearly subordinate to the 12.2:1 primary.
        '--mantine-color-default': '#1D1C19', // surface/card (dark[6]) — distinct from canvas
        '--mantine-color-default-hover': '#343330', // dark[5]
        '--mantine-color-default-border': '#51504D', // dark[4]
        '--mantine-color-default-color': '#D3D1CD', // match body text
        '--mantine-color-anchor': 'var(--mantine-color-sage-3)', // light sage links
        '--mantine-color-placeholder': '#767471', // dark[3]
        // Colored text decoupled from fills: shade 3 reads AA light-on-navy.
        '--mantine-color-error-text': '#FF96A0',
        '--mantine-color-error-light-color': '#FF96A0',
        '--mantine-color-warning-text': '#DBB26F',
        '--mantine-color-warning-light-color': '#DBB26F',
        '--mantine-color-success-text': '#83CF85',
        '--mantine-color-success-light-color': '#83CF85',
        '--mantine-color-info-text': '#96BDE9',
        '--mantine-color-info-light-color': '#96BDE9',
        '--ad-on-primary': '#FDFCF9', // white label on the deep sage primary (shade 6)
    },
})
